import { z } from "zod/v4";
import { defineResource, defineEndpoint } from "@visioni/a3s-client-api";
import { FEATURE_NAME, LOAD_STATUSES, TRAVEL_DIRECTIONS } from "../constants";

// ==================== Schemas ====================

/** A forensic flag as served by the vehicle-tracker API. */
export const ForensicFlag = z.union([z.boolean(), z.string(), z.number(), z.array(z.string()), z.null()]);

export type ForensicFlag = z.infer<typeof ForensicFlag>;

export const VehicleFeatures = z
  .object({
    signText: ForensicFlag.optional(),
    tray: ForensicFlag.optional(),
    trailer: ForensicFlag.optional(),
  })
  .loose();

export type VehicleFeatures = z.infer<typeof VehicleFeatures>;

// Allow either UTC (`Z`) or offset-bearing ISO timestamps from the upstream VT API.
const IsoTimestamp = z.iso.datetime({ offset: true });

export const VehicleDetection = z.object({
  id: z.string().optional(),
  createdAt: IsoTimestamp,
  assetId: z.string(),
  cameraId: z.string(),
  source: z.string(),
  plateText: z.string(),
  plateConfidence: z.number(),
  plateImageKey: z.string().nullable(),
  vehicleType: z.string(),
  make: z.string().nullable(),
  model: z.string().nullish(),
  appearanceDescription: z.string().nullish(),
  color: z.string().nullable(),
  // Absent until VT ships travel direction (vehicle-tracker!142); null when the source reports none.
  travelDirection: z.enum(TRAVEL_DIRECTIONS).nullish(),
  firstSeenAt: IsoTimestamp,
  lastSeenAt: IsoTimestamp,
  loadStatus: z.enum(LOAD_STATUSES).nullable(),
  loadContents: z.string().nullable(),
  imageUrls: z.array(z.url()),
  vehicleFeatures: VehicleFeatures.nullable(),
});

export type VehicleDetection = z.infer<typeof VehicleDetection>;

export const VehicleGroup = z.object({
  key: z.string(),
  tier: z.enum(["strong", "medium"]),
  score: z.number(),
  members: z.array(VehicleDetection),
});

export type VehicleGroup = z.infer<typeof VehicleGroup>;

export const FlatVehicleDetection = VehicleDetection.extend({
  groupId: z.string(),
  tier: z.enum(["strong", "medium"]),
  score: z.number(),
});

export type FlatVehicleDetection = z.infer<typeof FlatVehicleDetection>;

// The VT `/v3/vehicles` endpoint returns a single shape: a flat, row-keyset
// paginated vehicle list tagged with a server-computed `groupId` (ADR-0068).
export const ListVehiclesResponse = z.object({
  vehicles: z.array(FlatVehicleDetection),
  nextCursor: z.string().nullable(),
});

export type ListVehiclesResponse = z.infer<typeof ListVehiclesResponse>;

export const ListVehiclesQuery = z.object({
  startDate: z.string(),
  endDate: z.string(),
  assetIds: z.string().optional(),
  siteIds: z.string().optional(),
  cameraIds: z.string().optional(),
  types: z.string().optional(),
  plateText: z.string().optional(),
  plateSearchMode: z.enum(["exact", "fuzzy"]).optional(),
  plateMaxDistance: z.string().optional(),
  description: z.string().optional(),
  make: z.string().optional(),
  model: z.string().optional(),
  color: z.string().optional(),
  loadStatus: z.string().optional(),
  attributes: z.string().optional(),
  sort: z.enum(["asc", "desc"]).optional(),
  cursor: z.string().optional(),
  limit: z.string().optional(),
});

export type ListVehiclesQuery = z.infer<typeof ListVehiclesQuery>;

export const CameraAllocation = z.object({
  start: z.string(),
  end: z.string().nullable(),
});

export type CameraAllocation = z.infer<typeof CameraAllocation>;

export const CameraAssetGroup = z.object({
  assetId: z.string(),
  siteId: z.string().optional(),
  siteTimezone: z.string().nullable().optional(),
  cameras: z.array(z.string()),
  /** A3S nickname per camera in `cameras`, from the asset's most recent allocation that includes it. */
  cameraNicknames: z.record(z.string(), z.string().nullable()).optional(),
  allocations: z.array(CameraAllocation),
});

export type CameraAssetGroup = z.infer<typeof CameraAssetGroup>;

export const ListCamerasQuery = z.object({
  active: z.string().optional(),
  siteId: z.string().optional(),
});

export type ListCamerasQuery = z.infer<typeof ListCamerasQuery>;

export const ListCamerasResponse = z.object({
  cameras: z.array(CameraAssetGroup),
});

export type ListCamerasResponse = z.infer<typeof ListCamerasResponse>;

// ==================== Resources & Endpoints ====================

export const vehiclesResource = defineResource({
  feature: FEATURE_NAME,
  resource: "vehicles",
  scope: "tenant",
});

export const listVehiclesEndpoint = defineEndpoint(vehiclesResource, {
  responseSchema: ListVehiclesResponse,
  querySchema: ListVehiclesQuery,
});

export const camerasResource = defineResource({
  feature: FEATURE_NAME,
  resource: "cameras",
  scope: "tenant",
});

export const listCamerasEndpoint = defineEndpoint(camerasResource, {
  responseSchema: ListCamerasResponse,
  querySchema: ListCamerasQuery,
});
